import { Icons } from '@/components/icons';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInfiniteFilterList } from '../hooks/use-infinite-filter-list';
import type { GlobalFilterItem } from '../types';
import {
  GlobalFilerListItemSkeleton,
  GlobalFilterListItem,
} from './filter-list-item';

export function FilterRealEstates({
  search,
  onToggle,
  filteredItems,
}: {
  search: string;
  onToggle: (id: string, displayString: string) => void;
  filteredItems: GlobalFilterItem[];
}) {
  const {
    items: realEstates,
    isLoading,
    hasNextPage,
    sentryRef,
  } = useInfiniteFilterList(
    useInfiniteQuery(
      api.objects_realestate.query.infinite.queryOptions({
        params: {
          pageSize: 25,
          search: search || undefined,
        },
      })
    )
  );

  if (isLoading) {
    return Array.from({ length: 10 }).map((_, index) => (
      <GlobalFilerListItemSkeleton key={index}>
        <Skeleton className='h-3 w-40' />
      </GlobalFilerListItemSkeleton>
    ));
  }

  if (!realEstates.length) {
    return (
      <div className='flex h-full flex-col'>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Icons.realestate />
            </EmptyMedia>
            <EmptyTitle>Inga fastigheter hittades</EmptyTitle>
            <EmptyDescription>
              Vi kunde inte hitta några fastigheter.{' '}
              {search.length > 0 && 'Försök med en annan sökterm.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {realEstates.map((realEstate) => {
        const isSelected = Boolean(
          filteredItems.find(
            (fi) => fi.type === 'global_realestates' && fi.id === realEstate.id
          )
        );
        return (
          <GlobalFilterListItem
            key={realEstate.id}
            item={realEstate}
            isSelected={isSelected}
            onToggle={onToggle}
          >
            {realEstate.mainAddress && (
              <p className='mt-1 text-xs'>
                {`${realEstate.mainAddress.street}, ${realEstate.mainAddress.city}`}
              </p>
            )}
          </GlobalFilterListItem>
        );
      })}

      {hasNextPage && (
        <div ref={sentryRef}>
          <GlobalFilerListItemSkeleton>
            <Skeleton className='h-3 w-40' />
          </GlobalFilerListItemSkeleton>
        </div>
      )}
    </div>
  );
}
